import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const Task = () => {
  const { canCreate, canView, canUpdate, canDelete } =
    useModuleAccess("task_management");
  const savedFilters = JSON.parse(
    sessionStorage.getItem("taskFilters") || "{}",
  );
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [deleteTaskDesc, setDeleteTaskDesc] = useState("");
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [users, setUsers] = useState([]);
  const [assignedByFilter, setAssignedByFilter] = useState(
    savedFilters.assignedByFilter || "",
  );
  const [assignedToFilter, setAssignedToFilter] = useState(
    savedFilters.assignedToFilter || "",
  );
  const [taskStatusFilter, setTaskStatusFilter] = useState(
    savedFilters.taskStatusFilter || "",
  );
  const [isExporting, setIsExporting] = useState(false);

  const TASK_STATUS_OPTIONS = ["Inprogress", "Completed", "Hold", "Pending"];

  // Fetch users for filter dropdowns
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const payload = {
          action: "get-users",
          page: 1,
          limit: 1000,
        };
        const response = await axios.post(API_URL, payload);

        let responseData = response.data;
        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            responseData = response.data;
          }
        }

        if (responseData.success === true && responseData.users) {
          setUsers(responseData.users);
        }
      } catch (error) {
        console.error("Fetch users error:", error);
      }
    };

    fetchUsers();
  }, []);

  // Fetch tasks with server-side pagination
  const fetchTasks = useCallback(async () => {
    const userId = localStorage.getItem("id");
    if (!userId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        action: "get-all-tasks",
        status: "active",
        requestid: userId,
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
      };

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      if (assignedByFilter) {
        payload.assignedBy = assignedByFilter;
      }

      if (assignedToFilter) {
        payload.assignedTo = assignedToFilter;
      }

      if (taskStatusFilter) {
        payload.taskStatus = taskStatusFilter;
      }

      if (sortBy) {
        payload.sortBy = sortBy;
        payload.sortOrder = sortOrder;
      }

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (
        responseData.success === true &&
        responseData.data &&
        responseData.data.tasks
      ) {
        setTasks(responseData.data.tasks);
        setFilteredTasks(responseData.data.tasks);

        if (
          responseData.data.pagination &&
          responseData.data.pagination.total
        ) {
          setTotalCount(responseData.data.pagination.total);
        } else {
          setTotalCount(responseData.data.tasks.length);
        }
      } else {
        toast.error(responseData.message || "Failed to fetch tasks", {
          autoClose: 2000,
        });
        setTasks([]);
        setFilteredTasks([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch tasks error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message || "An error occurred while fetching tasks",
          { autoClose: 2000 },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
      setTasks([]);
      setFilteredTasks([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    assignedByFilter,
    assignedToFilter,
    taskStatusFilter,
    sortBy,
    sortOrder,
    navigate,
  ]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = tasks.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);
  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle filter changes
  const handleAssignedByChange = (e) => {
    setAssignedByFilter(e.target.value);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleAssignedToChange = (e) => {
    setAssignedToFilter(e.target.value);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleTaskStatusChange = (e) => {
    setTaskStatusFilter(e.target.value);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    setAssignedByFilter("");
    setAssignedToFilter("");
    setTaskStatusFilter("");
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Handle export with current filters

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const userId = localStorage.getItem("id");

      if (!userId) {
        toast.error("User session expired. Please login again.", {
          autoClose: 2000,
        });
        setTimeout(() => {
          navigate("/");
        }, 2000);
        return;
      }

      const payload = {
        action: "export-tasks",
        status: "active",
        requestid: userId,
      };

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      if (assignedByFilter) {
        payload.assignedBy = assignedByFilter;
      }

      if (assignedToFilter) {
        payload.assignedTo = assignedToFilter;
      }

      if (taskStatusFilter) {
        payload.taskStatus = taskStatusFilter;
      }

      const response = await axios.post(API_URL, payload, {
        responseType: "json",
      });

      let base64;
      if (response.data.body) {
        base64 = response.data.body;
      } else {
        base64 = response.data;
      }

      if (!base64 || !base64.startsWith("UEs")) {
        throw new Error("Invalid Excel data received");
      }

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);
      link.download = `task-export-${timestamp}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      toast.success("Export completed successfully", { autoClose: 2000 });
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data. Please try again.", {
        autoClose: 2000,
      });
    } finally {
      setIsExporting(false);
    }
  };
  // Handle search trigger
  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";

    if (trimmedQuery === "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    } else {
      setActiveSearchQuery(trimmedQuery);
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    const columnMap = {
      sno: "_id",
      taskDescription: "taskDescription",
      expectedDate: "expectedCompletedDate",
      assignedBy: "assignedBy",
      assignedTo: "assignedTo",
      taskStatus: "taskStatus",
      duration: "taskDuration",
    };

    const apiField = columnMap[column] || column;

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("createdAt");
        setSortOrder("desc");
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    setCurrentPage(1);
  };

  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      currentPage,
      itemsPerPage,
      assignedByFilter,
      assignedToFilter,
      taskStatusFilter,
    };

    sessionStorage.setItem("taskFilters", JSON.stringify(filters));
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    assignedByFilter,
    assignedToFilter,
    taskStatusFilter,
  ]);

  // Get sort indicator icon
  const getSortIcon = (column) => {
    const columnMap = {
      sno: "_id",
      taskDescription: "taskDescription",
      expectedDate: "expectedCompletedDate",
      assignedBy: "assignedBy",
      assignedTo: "assignedTo",
      taskStatus: "taskStatus",
      duration: "taskDuration",
    };

    const apiField = columnMap[column] || column;

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }
    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }
    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  // Handle delete button click
  const handleDeleteClick = (taskId, taskDesc) => {
    setDeleteTaskId(taskId);
    setDeleteTaskDesc(taskDesc);
  };

  // Handle delete confirmation
  const handleDelete = async () => {
    if (!deleteTaskId) {
      return;
    }

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return;
    }

    setIsDeleting(true);

    try {
      const payload = {
        action: "delete-task",
        id: deleteTaskId,
        requestid: currentUserId,
      };

      const response = await axios.post(API_URL, payload);

      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      if (responseData.success === true) {
        toast.success("Deleted successfully", { autoClose: 2000 });

        if (window.$) {
          window.$("#modaldeleteTask").modal("hide");
        }

        setDeleteTaskId(null);
        setDeleteTaskDesc("");

        if (tasks.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchTasks();
        }
      } else {
        toast.error(responseData.message || "Failed to delete task", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete task error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message || "An error occurred while deleting task",
          { autoClose: 2000 },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const formatDuration = (durationString) => {
    if (!durationString || durationString === "-") return "-";

    const daysMatch = durationString.match(/(\d+)\s*Days?/i);
    const hoursMatch = durationString.match(/(\d+)\s*Hours?/i);
    const minutesMatch = durationString.match(/(\d+)\s*Minutes?/i);

    const days = daysMatch ? parseInt(daysMatch[1]) : 0;
    const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
    const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;

    let result = [];

    if (days > 0) {
      result.push(`${days} ${days === 1 ? "Day" : "Days"}`);
    }

    if (hours > 0) {
      result.push(`${hours} ${hours === 1 ? "Hour" : "Hours"}`);
    }

    if (minutes > 0) {
      result.push(`${minutes} ${minutes === 1 ? "Minute" : "Minutes"}`);
    }

    if (result.length === 0) {
      return "0 Minutes";
    }

    return result.join(" ");
  };
  // Get task status color class
  const getTaskStatusColor = (status) => {
    if (!status) return "f_red";
    const statusLower = status.toLowerCase();

    switch (statusLower) {
      case "completed":
        return "f_green";
      case "pending":
        return "f_red";
      case "inprogress":
        return "text-info";
      case "hold":
        return "text-warning";
      default:
        return "f_red";
    }
  };

  // Truncate text
  const truncateText = (text, maxLength = 50) => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Tasks</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <Link to="/taskAdd" className="btn btn-blue">
                  Add
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card-body">
                <div className="row align-items-center mb-3">
                  <div className="col-md-3">
                    <div className="form-group mb-0">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search tasks..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                        />
                        <div className="input-group-append">
                          <div
                            className="input-group-text crsr_pntr"
                            onClick={handleSearchTrigger}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa-solid fa-search"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group mb-0">
                      <select
                        className="form-control"
                        value={assignedByFilter}
                        onChange={handleAssignedByChange}
                      >
                        <option value="">All Assigned By</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group mb-0">
                      <select
                        className="form-control"
                        value={assignedToFilter}
                        onChange={handleAssignedToChange}
                      >
                        <option value="">All Assigned To</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form-group mb-0">
                      <select
                        className="form-control"
                        value={taskStatusFilter}
                        onChange={handleTaskStatusChange}
                      >
                        <option value="">All Task Status</option>
                        {TASK_STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3 text-right">
                    {(activeSearchQuery ||
                      assignedByFilter ||
                      assignedToFilter ||
                      taskStatusFilter) && (
                      <button
                        className="btn btn-secondary btn-sm mr-2"
                        onClick={handleClearFilters}
                      >
                        <i className="fa-solid fa-times mr-1"></i>
                        Clear Filters
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={handleExport}
                      disabled={isExporting}
                    >
                      <i
                        className={`fa-solid ${isExporting ? "fa-spinner fa-spin" : "fa-file-export"} mr-1`}
                      ></i>
                      {isExporting ? "Exporting..." : "Export"}
                    </button>
                  </div>
                </div>
                {(activeSearchQuery ||
                  assignedByFilter ||
                  assignedToFilter ||
                  taskStatusFilter) && (
                  <div className="row">
                    <div className="col-md-12">
                      <p
                        className="mb-2 text-muted"
                        style={{ fontSize: "13px" }}
                      >
                        <i className="fa-solid fa-filter mr-1"></i>
                        Active Filters:
                        {activeSearchQuery && (
                          <span className="ml-2">
                            <strong>Search:</strong> "{activeSearchQuery}"
                          </span>
                        )}
                        {assignedByFilter && (
                          <span className="ml-2">
                            <strong>Assigned By:</strong>{" "}
                            {users.find((u) => u.id === assignedByFilter)
                              ?.name || assignedByFilter}
                          </span>
                        )}
                        {assignedToFilter && (
                          <span className="ml-2">
                            <strong>Assigned To:</strong>{" "}
                            {users.find((u) => u.id === assignedToFilter)
                              ?.name || assignedToFilter}
                          </span>
                        )}
                        {taskStatusFilter && (
                          <span className="ml-2">
                            <strong>Task Status:</strong> {taskStatusFilter}
                          </span>
                        )}
                        <span className="ml-2">
                          (Showing: {filteredTasks.length} of {totalCount})
                        </span>
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : tasks.length === 0 ? (
                <div className="nocontent_div text-center p-4">
                  There are no tasks yet.
                </div>
              ) : filteredTasks.length === 0 &&
                (activeSearchQuery ||
                  assignedByFilter ||
                  assignedToFilter ||
                  taskStatusFilter) ? (
                <div className="nocontent_div text-center p-4">
                  No tasks found matching your filters.
                  <div className="mt-3">
                    <button
                      className="btn btn-blue"
                      onClick={handleClearFilters}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="tasks"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr style={{ fontSize: "12px" }}>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("sno")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              S.No {getSortIcon("sno")}
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("taskDescription")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Task Description {getSortIcon("taskDescription")}
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("expectedDate")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Expected Date {getSortIcon("expectedDate")}
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("assignedBy")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Assigned By {getSortIcon("assignedBy")}
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("assignedTo")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Assigned To {getSortIcon("assignedTo")}
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Duration
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("taskStatus")}
                            style={{
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              fontSize: "12px",
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Status {getSortIcon("taskStatus")}
                            </span>
                          </th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTasks.map((task, index) => (
                          <tr
                            key={task._id || index}
                            style={{ fontSize: "12px" }}
                          >
                            <td style={{ fontSize: "12px" }}>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td
                              style={{ fontSize: "12px" }}
                              title={task.taskDescription}
                            >
                              {truncateText(task.taskDescription, 50)}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {formatDate(task.expectedCompletedDate)}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {task.assignedBy?.name || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {task.assignedTo?.name || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {formatDuration(task.taskDuration, true)}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              <span
                                className={`fwt_600 ${getTaskStatusColor(
                                  task.taskStatus,
                                )}`}
                              >
                                {task.taskStatus || "Pending"}
                              </span>
                            </td>
                            <td style={{ fontSize: "13px" }}>
                              <div className="d-flex justify-content-center align-items-center">
                                {canView && (
                                  <Link
                                    to={`/taskView/${task._id}`}
                                    className="crsr_pntr mr-2"
                                    title="View"
                                  >
                                    <i className="fa-solid fa-eye" />
                                  </Link>
                                )}
                                {canUpdate && (
                                  <Link
                                    to={`/taskEdit/${task._id}`}
                                    className="crsr_pntr mr-2"
                                    title="Edit"
                                  >
                                    <i className="fa-solid fa-pen" />
                                  </Link>
                                )}
                                {canDelete && (
                                  <a
                                    href="#modaldeleteTask"
                                    className="crsr_pntr action-icon-link"
                                    data-toggle="modal"
                                    data-target="#modaldeleteTask"
                                    onClick={() =>
                                      handleDeleteClick(
                                        task._id,
                                        task.taskDescription,
                                      )
                                    }
                                    title="Delete"
                                  >
                                    <i className="fa-solid fa-trash-can" />
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && tasks.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Task Modal */}
      <div
        className="modal fade"
        id="modaldeleteTask"
        tabIndex={-1}
        aria-labelledby="modaldeleteTaskLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteTaskLabel">
                Delete Task
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete this task? <br />
                <span className="fwt_600">
                  {truncateText(deleteTaskDesc, 50)}
                </span>
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Task;
